import type { ValidateFunction } from 'ajv';
import { prepareAjvErrors, upperCaseToCamelCase } from '../../utils';
import type {
  IConfigSource,
  GetValue,
  GetValueOption,
  GetValuesAll,
} from '../loader.types';

/**
 * Creates a configuration source from the environment in `process.env`.
 *
 * Example:
 * ```
 * const source = new EnvironmentSource(validators.backend, process.env);
 * const config = new ConfigLoader(source);
 * ```
 */
export class EnvironmentSource<T> implements IConfigSource<T> {
  private readonly config: T | undefined;

  /**
   * @param validator - The generated Ajv validator.
   * @param environment - The environment to fetch values from, usually `process.env`.
   */
  constructor(
    validator: ValidateFunction,
    environment: Record<string, string | undefined>,
  ) {
    const snapshot: Record<string, string | undefined> | undefined = {};
    for (const [key, value] of Object.entries(environment)) {
      // TEMP support both version of variables camelCase and SNAKE_CASE should be updated during the refactor
      snapshot[key.replace('NEXT_PUBLIC_', '')] = value;
      const camelCase = upperCaseToCamelCase(key.replace('NEXT_PUBLIC_', ''));
      snapshot[camelCase] = value;
    }

    // validate will modify `untrusted`.
    if (!validator(snapshot)) {
      const errors = prepareAjvErrors(validator);
      console.error(errors);
      throw new Error('config and schema mismatch');
    }

    this.config = snapshot as unknown as T;
  }

  getValue: GetValue<T> = (key) => {
    const value = this.config?.[key];
    if (value === undefined) {
      throw new Error(`key not found: ${key.toString()}`);
    }

    return value;
  };

  getValueOptional: GetValueOption<T> = (key) => {
    return this.config?.[key];
  };

  getValuesAll: GetValuesAll<T> = () => this.config;
}
